/**
 * Renderiza las opciones de respuesta de un anuncio.
 *
 * Variantes visuales:
 *   - modal   → botones grandes apilados verticalmente (CTA principal)
 *   - banner  → botones inline horizontales y compactos
 *   - card    → botones medianos en fila (con wrap si no caben)
 */

import { AnnouncementOption, AnnouncementUserResponse } from '@interfaces/general';
import { handleOptionAction } from './handleOptionAction';

type Variant = 'modal' | 'banner' | 'card';

interface Props {
  options: AnnouncementOption[];
  announcementId: string;
  userResponse: AnnouncementUserResponse | null;
  bgColor: string;
  textColor: string;
  isSubmitting: boolean;
  variant: Variant;
  respond: (announcementId: string, optionId: string, value: string) => Promise<unknown>;
  onComplete: () => void;
}

export const AnnouncementOptionButtons = ({
  options,
  announcementId,
  userResponse,
  bgColor,
  textColor,
  isSubmitting,
  variant,
  respond,
  onComplete,
}: Props) => {
  if (!options || options.length === 0) return null;

  const containerClass =
    variant === 'modal'
      ? 'flex flex-col gap-3 w-full mt-auto pt-6'
      : variant === 'banner'
        ? 'flex flex-row gap-2 flex-wrap items-center'
        : 'flex flex-row gap-2 flex-wrap';

  const sized = [...options].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className={containerClass}>
      {sized.map((option) => {
        const isCloseAction = option.action_type === 'close';
        const isSelected = userResponse?.option_id === option.id;

        const baseBtn =
          variant === 'modal'
            ? 'py-3 px-6 rounded-lg font-semibold text-base'
            : variant === 'banner'
              ? 'py-1.5 px-3 rounded-md font-semibold text-sm'
              : 'py-2 px-4 rounded-md font-semibold text-sm';

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => handleOptionAction({ option, announcementId, respond, onComplete })}
            disabled={isSubmitting}
            style={{
              backgroundColor: isCloseAction ? 'transparent' : isSelected ? '#4CAF50' : textColor,
              color: isCloseAction ? textColor : bgColor,
              border: isCloseAction ? `1px solid ${textColor}` : 'none',
              width: variant === 'modal' ? '100%' : undefined,
            }}
            className={`${baseBtn} transition-all hover:opacity-90 disabled:opacity-50`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
